import React from "react";
import { formatDateLabel, todayDateStr } from "../../classroomSchedule.js";

// `off` is a day the weekday filter leaves out (see the month timeline).
const STATUS_CLASS = { free: "free", occupied: "occupied", teaching: "occupied", maintenance: "maintenance", absent: "absent", off: "off" };

/**
 * Multi-day grid used by the Labs & Trainers timelines: one row per room
 * or trainer, one cell per date. Each cell shows a status colour plus a
 * readable label (the actual batch name, not a bare count) — click a cell
 * to open that room/trainer's detail popup for that specific day (no
 * refetch: the caller already has every date's data client-side from
 * `rangeSessions`). Cells are a fixed width and truncate long names with an
 * ellipsis (the full list is always in the title/tooltip); the container
 * scrolls horizontally rather than shrinking cells further.
 *
 * rows: [{ key, label, meta, cells: { [dateStr]: {status, text, title} } }]
 * onCellClick(dateStr, rowKey)
 */
// `onRowOpen(rowKey)`, when given, makes each row's name the bridge to that
// lab or trainer (Waypoint).
const GridTimeline = ({ dates, rows, onCellClick, onRowOpen, dense }) => {
  const today = todayDateStr();
  const cellWidth = dense ? 56 : 130;

  return (
    /* The two widths below are the only inline styles here, and both
       come from the data: how wide a day cell is in this density, and
       how wide the grid has to be to hold every date. */
    <div className="cls-tl-scroll">
      <div className={`cls-gridtl${dense ? " dense" : ""}`} style={{ minWidth: 180 + dates.length * cellWidth }}>
        <div className="cls-gridtl-row head">
          <div className="cls-gridtl-label" />
          {dates.map((d) => (
            <div key={d} className={`cls-gridtl-cell head${d === today ? " today" : ""}`} style={{ width: cellWidth }}>
              {dense ? formatDateLabel(d, { day: "numeric" }) : formatDateLabel(d, { weekday: "short", day: "numeric" })}
            </div>
          ))}
        </div>
        {rows.map((row) => (
          <div key={row.key} className="cls-gridtl-row">
            {onRowOpen ? (
              <button
                type="button"
                className="cls-gridtl-label cls-gridtl-label--open"
                onClick={() => onRowOpen(row.key)}
                title={`Open ${row.label}`}
              >
                <span className="lp-name">{row.label}</span>
                {row.meta && <span className="lp-sub">{row.meta}</span>}
              </button>
            ) : (
              <div className="cls-gridtl-label">
                <span className="lp-name">{row.label}</span>
                {row.meta && <span className="lp-sub">{row.meta}</span>}
              </div>
            )}
            {dates.map((d) => {
              const cell = row.cells[d] || { status: "free", text: "", title: "Free" };
              return (
                <button
                  type="button"
                  key={d}
                  className={`cls-gridtl-cell ${STATUS_CLASS[cell.status] || "free"}${d === today ? " today" : ""}`}
                  style={{ width: cellWidth }}
                  title={`${formatDateLabel(d, { weekday: "long", month: "short", day: "numeric" })} — ${cell.title}`}
                  onClick={() => onCellClick(d, row.key)}
                >
                  <span className="cls-gridtl-cell-text">{cell.text}</span>
                </button>
              );
            })}
          </div>
        ))}
        {!rows.length && <p className="pgm-empty cls-tl-empty">Nothing to show.</p>}
      </div>
    </div>
  );
};

export default GridTimeline;
